import { ABHA_ADDRESS_PATTERN, ABHA_NUMBER_PATTERN } from './abha-login.util';
import { AbhaEnrolmentResult } from './iabha-info';

type AbhaQrPayload = Readonly<Record<string, unknown>>;

export function parseAbhaQr(qrText: string): AbhaEnrolmentResult | null {
  const trimmed = qrText.trim();
  const payload = tryParsePayload(trimmed);
  if (payload) {
    return readProfile(payload);
  }
  const number = trimmed.replace(/\s/g, '');
  if (ABHA_NUMBER_PATTERN.test(number)) {
    return { abhaNumber: number, abhaAddress: '', firstName: '', isNew: false };
  }
  return ABHA_ADDRESS_PATTERN.test(trimmed)
    ? { abhaNumber: '', abhaAddress: trimmed, firstName: '', isNew: false }
    : null;
}

export function hasCompleteAbhaProfile(profile: AbhaEnrolmentResult): boolean {
  return ABHA_NUMBER_PATTERN.test(profile.abhaNumber)
    && ABHA_ADDRESS_PATTERN.test(profile.abhaAddress)
    && !!profile.firstName.trim()
    && /^[MFO]$/i.test(profile.gender || '')
    && isValidDateOfBirth(profile.dateOfBirth || '');
}

function readProfile(payload: AbhaQrPayload): AbhaEnrolmentResult | null {
  const abhaNumber = readString(payload, ['hidn', 'healthIdNumber', 'abhaNumber']);
  const abhaAddress = readString(payload, ['phr', 'hid', 'healthId', 'abhaAddress']);
  if ((!abhaNumber && !abhaAddress)
    || (abhaNumber && !ABHA_NUMBER_PATTERN.test(abhaNumber))
    || (abhaAddress && !ABHA_ADDRESS_PATTERN.test(abhaAddress))) {
    return null;
  }
  const nameParts = readString(payload, ['name']).split(/\s+/).filter(Boolean);
  return {
    abhaNumber, abhaAddress, firstName: nameParts[0] || '',
    middleName: nameParts.length > 2 ? nameParts.slice(1, -1).join(' ') : '',
    lastName: nameParts.length > 1 ? nameParts[nameParts.length - 1] : '',
    gender: readString(payload, ['gender']), dateOfBirth: readDateOfBirth(payload),
    mobile: readString(payload, ['mobile', 'mobileNumber']), isNew: false,
  };
}

function readString(payload: AbhaQrPayload, keys: readonly string[]): string {
  for (const key of keys) {
    const value = payload[key];
    if (typeof value === 'string' && value.trim()) {
      return value.trim();
    }
  }
  return '';
}

function readDateOfBirth(payload: AbhaQrPayload): string {
  const date = readString(payload, ['dob', 'dateOfBirth']);
  const year = readString(payload, ['yearOfBirth']);
  return date || (year
    ? `${readString(payload, ['dayOfBirth']) || '1'}-${readString(payload, ['monthOfBirth']) || '1'}-${year}`
    : '');
}

function isValidDateOfBirth(value: string): boolean {
  const parts = value.replace(/\//g, '-').split('-');
  if (parts.length === 1 && /^\d{4}$/.test(value)) {
    return Number(value) >= 1800 && Number(value) <= new Date().getFullYear();
  }
  if (!/^\d{1,4}-\d{1,2}-\d{1,4}$/.test(value.replace(/\//g, '-'))) {
    return false;
  }
  const [year, month, day] = (parts[0].length === 4 ? parts : [...parts].reverse()).map(Number);
  const date = new Date(year, month - 1, day);
  return year >= 1800 && date <= new Date()
    && date.getFullYear() === year && date.getMonth() === month - 1 && date.getDate() === day;
}

function tryParsePayload(qrText: string): AbhaQrPayload | null {
  try {
    const value: unknown = JSON.parse(qrText);
    return typeof value === 'object' && value !== null && !Array.isArray(value)
      ? value as AbhaQrPayload : null;
  } catch {
    return null;
  }
}
